import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Content, ContentPaginationResponse } from "../features/api/openData";
import { buildSubTagRankingContentsParams } from "../utils/newsRanking";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { QUERY_KEYS } from "@config/constant";

const PAGE_SIZE = 20;

export interface UseHotNewsBySubTagOptions {
  /** 二级主题 ID，与 getRankingContents 的 themeIds 一致 */
  subTagId: string;
  /** 语言 locale，须与服务端 getNewsBySubTag 传入的 locale 一致 */
  locale: string;
  enabled?: boolean;
  /** 每页条数，默认 20；建议与 SSR 的 pageSize 一致 */
  limit?: number;
  /** 初始数据，通常来自服务端渲染 */
  initialData?: {
    data: Content[];
    total: number;
  };
}

type SubTagNewsPageParam = { offset: number };

/**
 * 按子标签（themeId）拉取 Insight 新闻列表的分页 Hook。
 * 请求参数与 {@link buildSubTagRankingContentsParams} / SSR `getNewsBySubTag` 对齐，避免复用 `useHotNews` 时拼大量 initialParams。
 */
export function useHotNewsBySubTag(options: UseHotNewsBySubTagOptions) {
  const {
    subTagId,
    locale,
    enabled = true,
    limit: limitFromOptions,
    initialData,
  } = options;
  const { openDataApi } = useOspApiClientProvider();
  const limit = limitFromOptions ?? PAGE_SIZE;

  const queryKey = useMemo(
    () =>
      [
        QUERY_KEYS.HOT_NEWS_QUERY_KEY,
        "subTag",
        subTagId,
        locale,
        limit,
      ] as const,
    [subTagId, locale, limit],
  );

  const {
    data,
    isLoading,
    isFetching,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery<
    ContentPaginationResponse,
    Error,
    Content[],
    typeof queryKey,
    SubTagNewsPageParam
  >({
    queryKey: [...queryKey],
    enabled: enabled && Boolean(subTagId) && Boolean(openDataApi),
    initialData:
      initialData &&
      ({
        pages: [
          {
            code: 0,
            msg: "success",
            data: {
              limit,
              total: initialData.total,
              offset: 0,
              rows: initialData.data,
            },
          },
        ],
        pageParams: [{ offset: 0 }],
      } as const),
    queryFn: async ({ pageParam }) => {
      if (!openDataApi) {
        throw new Error("openDataApi is not defined");
      }
      const queryParams = buildSubTagRankingContentsParams(subTagId, locale, {
        offset: pageParam.offset,
        limit,
      });
      const response =
        await openDataApi.contents.getRankingContents(queryParams);
      if (!response.data) {
        throw new Error("获取新闻数据失败");
      }
      return response.data;
    },
    initialPageParam: { offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.data?.total) {
        return undefined;
      }
      if (allPages.length === 0) {
        return { offset: 0 };
      }
      const pageLimit = lastPage?.data?.limit ?? limit;
      if (allPages.length * pageLimit >= (lastPage?.data?.total ?? 0)) {
        return undefined;
      }
      return { offset: allPages.length * pageLimit };
    },
    select: (infiniteData) =>
      infiniteData?.pages?.flatMap((page) => page?.data?.rows || []) || [],
  });

  return {
    data: data || [],
    isLoading,
    isFetching,
    isPending,
    error,
    fetchNextPage,
    hasNextPage: hasNextPage || false,
    refetch,
    isFetchingNextPage,
  };
}
